import React, { useEffect, useState } from 'react';
import { Input, Modal } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { OFFLINE_REASON_MAX_LENGTH } from '../utils/activityFormUtils';
import { useImeSafeCount } from '../hooks/useImeSafeCount';

interface OfflineModalProps {
    open: boolean;
    loading?: boolean;
    onCancel: () => void;
    onOk: (reason: string) => Promise<void> | void;
}

/** 下线确认（原型页面 4）：原因必填 ≤256，红色警示「下线后活动不可再操作上线」 */
const OfflineModal: React.FC<OfflineModalProps> = ({ open, loading, onCancel, onOk }) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    // 输入法选字期间计数不超限、选完字截断到上限
    const reasonIme = useImeSafeCount(OFFLINE_REASON_MAX_LENGTH);

    useEffect(() => {
        if (open) {
            setReason('');
            setTouched(false);
        }
    }, [open]);

    const trimmed = reason.trim();
    const invalid = !trimmed || trimmed.length > OFFLINE_REASON_MAX_LENGTH;

    const handleOk = async () => {
        setTouched(true);
        if (invalid) return;
        await onOk(trimmed);
    };

    return (
        <Modal
            open={open}
            width={460}
            title={
                <span className="flex items-center gap-[10px] text-[17px]">
                    <ExclamationCircleFilled style={{ color: '#F0453A' }} />
                    {t('driverOps.activity.offlineModal.title')}
                </span>
            }
            okText={t('driverOps.activity.offlineModal.ok')}
            cancelText={t('common.cancel')}
            okButtonProps={{ danger: true, type: 'primary', loading }}
            onOk={handleOk}
            onCancel={onCancel}
            destroyOnClose
        >
            <div className="text-[14px] text-[#1F1F1F] mb-[6px]">
                <span className="text-[#F0453A] mr-[3px]">*</span>
                {t('driverOps.activity.offlineModal.reason')}
            </div>
            <Input.TextArea
                {...reasonIme.inputProps}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder={t('driverOps.activity.offlineModal.reasonPlaceholder') as string}
                showCount
                autoSize={{ minRows: 3, maxRows: 6 }}
                status={touched && invalid ? 'error' : undefined}
            />
            {touched && invalid && (
                <div className="text-[12.5px] text-[#F0453A] mt-[6px]">{t('driverOps.activity.offlineModal.reasonRequired')}</div>
            )}
            <div className="text-[12.5px] text-[#8C8C8C] mt-[6px]">{t('driverOps.activity.offlineModal.settleHint')}</div>
            <div className="bg-[#FFF1F0] border border-[#FFCCC7] rounded-lg px-3 py-2 mt-[10px] text-[#F0453A] font-semibold">
                {t('driverOps.activity.offlineModal.warning')}
            </div>
        </Modal>
    );
};

export default OfflineModal;
